import { type FC } from 'react'
import { FaTrash } from 'react-icons/fa'

import {
  Alert,
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicThead,
  BasicTr,
  Button,
  DateFormatter,
  Modal,
  ModalBody,
  ModalFooter
} from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  LABELS,
  MENU,
  UNITS
} from '@/constants/common/message.constants'
import { type ShipmentOrdersListExEntity } from '@/libs/api'

const DATE_FORMAT = 'yyyy/MM/dd'

type Props = {
  /** 削除対象。配車済みが混ざらない前提（呼び出し側が未配車のときだけボタンを出す） */
  orders: ShipmentOrdersListExEntity[]
  deleting: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * VOC_PJ-445 #1864: 配送依頼一覧から選択した依頼をまとめて物理削除する確認モーダル。
 *
 * 削除は元に戻せないため、対象の一覧を見せてから実行させる。取消（復元できる）と
 * 取り違えないよう、ステータス更新への導線も併記する。
 *
 * インポート由来（isManual = false）が1件でも混ざる場合は注意書きを出す。
 * このモーダル自体が確認のひと手間なので、リンククリックによる二段確認は設けない
 * （PR #4887 レビュー指摘）。
 */
export const BulkDeleteShipmentOrdersModal: FC<Props> = ({
  orders,
  deleting,
  onCancel,
  onConfirm
}) => {
  const hasImported = orders.some((order) => !order.isManual)
  const hasBundled = orders.some((order) => typeof order.bundleId === 'number')

  const canDelete = orders.length > 0

  return (
    <Modal
      title={MENU.BULK_DELETING_SHIPMENT_ORDERS}
      width={600}
      open
      onCancel={deleting ? undefined : onCancel}
    >
      <ModalBody>
        <p className="mb-2">
          {DESCRIPTIONS.BULK_DELETE_SHIPMENT_ORDERS_DESCRIPTION}
        </p>
        <p className="mb-4 text-secondary">
          {DESCRIPTIONS.BULK_DELETE_SHIPMENT_ORDERS_REVOKE_HINT}
        </p>

        {hasBundled && (
          <Alert color="yellow" className="mb-4" title="">
            {DESCRIPTIONS.BULK_DELETE_SHIPMENT_ORDERS_BUNDLE_NOTICE}
          </Alert>
        )}

        {hasImported && (
          <Alert color="red" className="mb-4" title="">
            {DESCRIPTIONS.INCLUDES_IMPORTED_SHIPMENT_ORDERS}
          </Alert>
        )}

        <p className="mb-2 text-sm font-semibold">
          {`${MENU.DELETING_SHIPMENT_ORDER}: ${orders.length}${UNITS.QUANTITY.NUMBER}`}
        </p>
        <div className="max-h-40 overflow-y-auto rounded border border-gray-200">
          <BasicTable className="w-full">
            {/* 3列が何の値かを見出しで示す（日付は発日・着日が紛らわしいため特に必要）。
                一覧はスクロールするので見出しは上端に貼り付ける */}
            <BasicThead className="sticky top-0 bg-white">
              <BasicTr>
                <BasicTh padding={2} className="w-[160px]">
                  {LABELS.ORDER_NO}
                </BasicTh>
                <BasicTh padding={2}>{LABELS.CUSTOMER}</BasicTh>
                <BasicTh padding={2} className="w-[120px]">
                  {LABELS.SHIPPING_DATE_SHORT}
                </BasicTh>
              </BasicTr>
            </BasicThead>
            <BasicTbody>
              {orders.map((order) => (
                <BasicTr key={order.id}>
                  <BasicTd padding={2}>{order.orderNo}</BasicTd>
                  <BasicTd padding={2}>{order.group.customer.name}</BasicTd>
                  <BasicTd padding={2}>
                    <DateFormatter
                      value={order.group.shippingDate}
                      dateFormat={DATE_FORMAT}
                    />
                  </BasicTd>
                </BasicTr>
              ))}
            </BasicTbody>
          </BasicTable>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel} disabled={deleting}>
          {ACTIONS.CANCEL}
        </Button>
        <Button
          data-testid="bulk-delete-shipment-orders-confirm"
          type="primary"
          danger
          icon={<FaTrash />}
          loading={deleting}
          disabled={!canDelete || deleting}
          onClick={onConfirm}
        >
          {ACTIONS.DELETE}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
